import {
  Swiper,
  SwiperSlide,
  SwiperOptions,
  Navigation,
  Thumbs,
  FreeMode,
} from '@/components/ui/slider-old';
import Image from '@/components/ui/image';
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from '@/components/icons/chevron-left';
import { ChevronRight } from '@/components/icons/chevron-right';
import placeholder from '@/assets/images/placeholders/product.svg';

interface Props {
  gallery: any[];
  isVideoProduct: boolean;
  className?: string;
  titlePrefixGallery?: string;
  titlePrefixThumbGallery?: string;
  altPrefixThumbGallery?: string;
  altPrefixGallery?: string;
}

const swiperParams: SwiperOptions = {
  slidesPerView: 1,
  spaceBetween: 0,
};

export default function ProductThumbnailGallery({
  gallery,
  isVideoProduct,
  className = 'w-full',
  titlePrefixGallery = 'View Product thumb gallery',
  titlePrefixThumbGallery = 'View Product thumb gallery',
  altPrefixThumbGallery = 'Product thumb gallery',
  altPrefixGallery = 'Product gallery',
}: Props) {
  let [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    if (isVideoProduct) {
      videoRefs.current.forEach((videoRef) => {
        if (videoRef) {
          videoRef.play().catch((error) => {
            console.log('Video autoplay failed:', error);
          });
        }
      });
    }
  }, [isVideoProduct]);

  return (
    <div className={className}>
      <div className="relative mb-3 w-full overflow-hidden xl:mb-5">
        <Swiper
          id="productGallery"
          speed={400}
          allowTouchMove={true}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          modules={[Navigation, Thumbs, FreeMode]}
          navigation={{
            prevEl: prevRef.current!,
            nextEl: nextRef.current!,
          }}
          {...swiperParams}
        >
          {gallery?.map((item: any, index) => (
            <SwiperSlide
              key={`product-gallery-${item.id}`}
              className="relative flex aspect-[1/1] max-w-[580px] items-center justify-center bg-light-200 dark:bg-dark-200"
            >
              {isVideoProduct ? (
                <div className="flex items-center justify-center w-full h-full">
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={item.original}
                    className="max-w-full max-h-full object-contain"
                    loop
                    muted
                    playsInline
                    autoPlay
                  />
                </div>
              ) : (
                <Image
                  fill
                  src={item?.original ?? placeholder}
                  alt={`${altPrefixGallery} ${index + 1}`}
                  title={`${titlePrefixGallery} image example ${index + 1}`}
                  className="object-contain"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  // width={350}
                  // height={350}
                  priority={index === 0}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute top-2/4 z-10 flex w-full items-center justify-between px-2.5 xl:px-4">
          <div
            ref={prevRef}
            className="flex h-8 w-8 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border border-light-400 bg-light text-dark/90 shadow-xl duration-300 transition hover:bg-light-200 hover:text-brand-dark focus:outline-none rtl:rotate-180 xl:h-9 xl:w-9"
          >
            <ChevronLeft className="h-4 w-4 xl:h-[18px] xl:w-[18px]" />
          </div>
          <div
            ref={nextRef}
            className="flex h-8 w-8 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border border-light-400 bg-light text-dark/90 shadow-xl duration-300 transition hover:bg-light-200 hover:text-brand-dark focus:outline-none rtl:rotate-180 xl:h-9 xl:w-9"
          >
            <ChevronRight className="h-4 w-4 xl:h-[18px] xl:w-[18px]" />
          </div>
        </div>
      </div>
      <div className="flex-shrink-0">
        <Swiper
          id="productGalleryThumbs"
          freeMode={true}
          modules={[Navigation, Thumbs, FreeMode]}
          observer={true}
          slidesPerView={4}
          onSwiper={setThumbsSwiper}
          observeParents={true}
          watchSlidesProgress={true}
        >
          {gallery?.map((item: any, index) => (
            <SwiperSlide
              key={`product-thumb-gallery-${item.id}`}
              className="relative flex aspect-[1/1] cursor-pointer items-center justify-center border border-light-500 transition hover:opacity-75 dark:border-dark-500"
            >
              {isVideoProduct ? (
                <div className="flex items-center justify-center w-full h-full">
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={item.original}
                    className="max-w-full max-h-full object-contain"
                    loop
                    muted
                    playsInline
                    autoPlay
                  />
                </div>
              ) : (
                <Image
                  /* fill */
                src={item?.thumbnail ?? placeholder}
                // alt={`Product thumb gallery ${item.id}`}
                alt={`${altPrefixThumbGallery} for image example ${index + 1}`}
                title={`${titlePrefixThumbGallery} image example ${index + 1}`}
                className="object-contain"
                width={116}
                height={116}
                priority
                loading="eager"
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
